import { useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import CasesTableDataRow from './CasesTableDataRow';

import { exportCases, getCases } from '../../api/casesApi/casesApi';
import TableContainer from '../../components/Table/TableContainer';
import Loader from '../../components/Loader';
import { CASES_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, CaseShort, FilterTypes } from '../../types';
import NoDataText from '../../components/NoDataText';
import TableFilters from '../../components/TableFilters/TableFilters';
import TableActions from '../../components/Table/TableActions';
import { CASES_STATUS_FILTER_OPTIONS } from '../../components/TableFilters/constants';
import useSnackBar from '../../hooks/useSnackBar';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { saveCsv } from '../../helpers/saveCsv';
import { useHasPermission } from '../../hooks/useHasPermission';

const CasesView = () => {
  const params = useParams();
  const location = useLocation();

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Cases', 'Create');

  const [casesList, setCasesList] = useState<CaseShort[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [filters, setFilters] = useState<FilterTypes>({
    clientId: '',
    defendantId: '',
    caseTypeId: '',
  });

  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    name: {
      order: '',
      fieldName: 'name',
    },
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    filedAt: {
      order: '',
      fieldName: 'filedAt',
    },
    sol: {
      order: '',
      fieldName: 'sol',
    },
    updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { firmId, ...filtersWithoutFirmId } = filters;
    getCases({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filtersWithoutFirmId, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data.resultSet) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setCasesList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField]);

  const exportList = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { firmId, ...filtersWithoutFirmId } = filters;
    exportCases({
      match: match,
      filters: { ...filtersWithoutFirmId, ...(hasParams && params) },
      activeSortField,
    })
      .then((response) => {
        saveCsv(response.data, 'Cases_List');
        setAlert({
          message: 'Settlements exported successfully.',
          type: 'success',
        });
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        }));
      });
  };

  return (
    <>
      {isDataLoading && !casesList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreate ? '/cases/create-case' : undefined}
            exportList={exportList}
            setMatch={setMatch}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              timeFilter={'Created'}
              statusFilter={CASES_STATUS_FILTER_OPTIONS}
              client
              defendant={!location.pathname.includes('/defendants')}
              match={match}
              caseType
            />
          )}

          {!isDataLoading && casesList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={CASES_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <CasesTableDataRow tableData={casesList} />
            </TableContainer>
          )}
          <Outlet />
        </>
      )}
    </>
  );
};

export default CasesView;
